import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import {
  cleanRequiredName,
  sanitizeReorderList,
  isFolderCacheFresh,
  isValidHexColor,
} from '@modules/aawz-intelligence-v2/utils/folderApiHelpers'

const ERR_INESPERADO =
  'Não foi possível concluir a operação de pastas. Tente novamente mais tarde ou contate o suporte.'
const TTL_MS = MINUTES_TIMEOUT_REQUESTS * 60 * 1000

class APIFolders extends APITemplate {
  constructor() {
    super()

    this.MODULE = 'aawz-intelligence-v2'
    // Dedupe de chamadas simultâneas de carregar (múltiplas superfícies montam
    // o Conversations ~ao mesmo tempo). Espelha apiCrmFunilSteps.reqCarregar.
    this.reqCarregar = null

    // STORE KEYS
    this.KEY_DATA = `${this.MODULE}/folders`
    this.KEY_LOADING = `${this.MODULE}/foldersLoading`
    this.KEY_LAST_UPDATE = `${this.MODULE}/foldersLastUpdate`

    // STORE ACTIONS (setters puros — a action Vuex NUNCA faz fetch)
    this.ACTION_SET_DATA = `${this.MODULE}/setFolders`
    this.ACTION_SET_LOADING = `${this.MODULE}/setFoldersLoading`

    // Persona da última carga bem-sucedida. Estado de INSTÂNCIA (não de módulo):
    // é o mesmo motivo de `reqCarregar` morar aqui.
    this.personaCarregada = undefined
  }

  // ---------- HELPERS ----------
  async getEarlyResponse(forceUpdate, persona) {
    if (this.reqCarregar) {
      const r = await this.reqCarregar
      return r
    }

    // O cache passou a depender da PERSONA: desde que o contador de cada pasta
    // é contado no universo dela, a mesma lista tem números diferentes por
    // agente. Sem esta checagem, abrir o Consultor (tudo 0) e depois o popup do
    // IA Operacional servia os zeros do Consultor pelos 30 min do TTL.
    if (persona !== this.personaCarregada) return null

    if (
      !forceUpdate &&
      isFolderCacheFresh(store.getters[this.KEY_LAST_UPDATE], TTL_MS)
    ) {
      return this.buildSuccessResponse({
        value: this.clonarDoStore(),
        message: '',
      })
    }

    return null
  }

  // Clone, não a referência: o getter devolve o array VIVO do Vuex, e quem
  // ordenar/mutar `r.value` corromperia `state.folders` em silêncio.
  clonarDoStore() {
    return JSON.parse(JSON.stringify(store.getters[this.KEY_DATA] || []))
  }

  setDataFromResponse(r) {
    if (r && r.status === 200) {
      store.dispatch(this.ACTION_SET_DATA, r.value || [])
    }
  }

  // ---------- REQUESTS ----------
  // `persona` viaja junto: o contador de cada pasta e a lista de conversas
  // precisam olhar o MESMO universo. Sem ela o backend conta as conversas de
  // todas as personas, e uma pasta com 3 conversas do IA Operacional aparecia
  // com o chip "3" no painel do Consultor — e abria vazia.
  async getFolders({ persona, todosAgentes } = {}) {
    const url =
      intelligenceV2.folders +
      formatQueryParams({
        persona: todosAgentes ? undefined : persona,
        todos_agentes: todosAgentes ? 'true' : undefined,
      })
    const r = await con.get(url)
    return r || this.buildErrorResponse({ message: ERR_INESPERADO })
  }

  // Loader: busca as pastas e despacha para a store (setter puro). A rede
  // vive aqui, na camada de API — a action Vuex só comita.
  async carregar({ forceUpdate = false, persona, todosAgentes } = {}) {
    // Com `todosAgentes` a chave do cache é a ausência de persona — é o que
    // faz a lista de todos os agentes não ser servida a quem pediu uma só.
    const chave = todosAgentes ? null : persona
    const earlyResponse = await this.getEarlyResponse(forceUpdate, chave)
    if (earlyResponse) return earlyResponse

    store.dispatch(this.ACTION_SET_LOADING, true)
    this.reqCarregar = this.getFolders({ persona, todosAgentes })
    try {
      const r = await this.reqCarregar
      this.setDataFromResponse(r)
      // Só depois do sucesso: uma falha não pode marcar o cache como sendo
      // desta persona, senão a próxima tentativa é servida pelo TTL.
      if (r && r.status === 200) this.personaCarregada = chave
      return r
    } finally {
      store.dispatch(this.ACTION_SET_LOADING, false)
      this.reqCarregar = null
    }
  }

  // color e opcional — quando ausente, o backend escolhe via round-robin
  // (constants/colors.py); quando informado, precisa ser um hex valido.
  async createFolder({ name, color } = {}) {
    const clean = cleanRequiredName(name)
    if (!clean) {
      return this.buildErrorResponse({
        message: 'Informe um nome para a pasta antes de salvar.',
        status: 400,
      })
    }
    if (color !== undefined && !isValidHexColor(color)) {
      return this.buildErrorResponse({
        message: 'Cor inválida. Escolha uma cor no seletor.',
        status: 400,
      })
    }
    const data = { name: clean }
    if (color !== undefined) data.color = color
    const r = await con.post(intelligenceV2.folders, data)
    return r || this.buildErrorResponse({ message: ERR_INESPERADO })
  }

  // color e opcional/livre (v-color-picker) — quando informado, precisa ser
  // um hex valido (#RRGGBB); o backend faz a mesma validacao solta.
  async renameFolder({ id, name, color } = {}) {
    if (!id) {
      return this.buildErrorResponse({
        message: 'Pasta inválida. Recarregue a página e tente novamente.',
        status: 400,
      })
    }
    const clean = cleanRequiredName(name)
    if (!clean) {
      return this.buildErrorResponse({
        message: 'Informe um nome para a pasta antes de salvar.',
        status: 400,
      })
    }
    if (color !== undefined && !isValidHexColor(color)) {
      return this.buildErrorResponse({
        message: 'Cor inválida. Escolha uma cor no seletor.',
        status: 400,
      })
    }
    const data = { name: clean }
    if (color !== undefined) data.color = color
    const r = await con.patch(intelligenceV2.folder(id), data)
    return r || this.buildErrorResponse({ message: ERR_INESPERADO })
  }

  async deleteFolder({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({
        message: 'Pasta inválida. Recarregue a página e tente novamente.',
        status: 400,
      })
    }
    const r = await con.delet(intelligenceV2.folder(id))
    return r || this.buildErrorResponse({ message: ERR_INESPERADO })
  }

  // S1 — reordenação. O backend expõe PATCH /folders/<id> com { sort_order };
  // `orders` é [{ id, sort_order }]. UI de reorder é follow-up.
  async reorderFolders({ orders } = {}) {
    const list = sanitizeReorderList(orders)
    if (!list.length) {
      return this.buildErrorResponse({
        message: 'Nenhuma pasta para reordenar.',
        status: 400,
      })
    }
    let last = null
    for (const item of list) {
      last = await con.patch(intelligenceV2.folder(item.id), {
        sort_order: item.sort_order,
      })
    }
    return last || this.buildErrorResponse({ message: ERR_INESPERADO })
  }

  // Move a conversa para uma pasta (PATCH na conversa). `id_folder` pode ser
  // null para desagrupar (voltar a "sem pasta"). Contrato §5.5.
  async moveConversation({ id, id_folder } = {}) {
    if (!id) {
      return this.buildErrorResponse({
        message: 'Conversa inválida. Recarregue a página e tente novamente.',
        status: 400,
      })
    }
    const r = await con.patch(intelligenceV2.conversation(id), { id_folder })
    return r || this.buildErrorResponse({ message: ERR_INESPERADO })
  }
}

export const apiFolders = new APIFolders()
